import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { get, pget } from "api";
import { Models } from "api/types";
import { PersonalisationConcerns } from "modules/personalisation/components/PersonalisationConcerns";
import { MeetYourAIAdvisorContainer_PATH } from "modules/personalisation/containers/MeetYourAIAdvisorContainer";
import { PersonalisationAdvisorBenefitsContainer_PATH } from "modules/personalisation/containers/PersonalisationAdvisorBenefitsContainer";
import { usePersonalisationData } from "modules/personalisation/context/PersonalisationDataContext";
import { usePersonalisationLeave } from "modules/personalisation/context/PersonalisationLeaveContext";
import {
  ConcernCategoryGroup,
  groupConcernsByCategory,
} from "modules/personalisation/utils/concernGrouping";
import { hasSkincareConcerns } from "modules/personalisation/utils/skincareConcerns";

import { HeritageContainer_PATH } from "./HeritageContainer";
import { PreparingScreenContainer_PATH } from "./PreparingScreenContainer";

export const ImproveConcernsContainer_PATH = "/personalise/concerns" as const;

const getConcernsForLocation = pget("/platform-location/{locationId}/concerns");

export function ImproveConcernsContainer() {
  const navigate = useNavigate();
  const { concerns, setConcernsAnswered, skipConcerns, advisor } =
    usePersonalisationData();
  const { openConfirmLeave } = usePersonalisationLeave();

  const [concernCategories, setConcernCategories] = useState<
    ConcernCategoryGroup[]
  >([]);

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user") as Promise<Models<"UserModel1">>,
    staleTime: 1000 * 60 * 2,
  });

  const { data: apiConcerns, isLoading } = useQuery({
    ...getConcernsForLocation.keyed({
      locationId: user?.platformLocation?.id as string,
    }),
    enabled: !!user?.platformLocation?.id,
    staleTime: 1000 * 60 * 5,
  });

  const concernsData = apiConcerns?.concerns || [];

  useEffect(() => {
    const loadConcernCategories = async () => {
      if (concernsData.length > 0) {
        const grouped = await groupConcernsByCategory(
          concernsData,
          user?.gender
        );
        setConcernCategories(grouped);
      }
    };

    loadConcernCategories();
  }, [concernsData, user?.gender]);

  const savedConcernIds = concerns.state === "answered" ? concerns.value : [];
  const initialSelections = concernsData.filter((concern) =>
    savedConcernIds.includes(concern.id)
  );

  if (isLoading) return null;

  // Check if practice offers any skincare concerns to determine navigation
  // see https://app.asana.com/1/1200167319116526/project/1210177794071597/task/1211771619790922
  const practiceHasSkincareConcerns = hasSkincareConcerns(concernsData);
  const nextPath = practiceHasSkincareConcerns
    ? HeritageContainer_PATH
    : PreparingScreenContainer_PATH;

  return (
    <PersonalisationConcerns
      concernCategories={concernCategories}
      initialSelections={initialSelections}
      progressPercentage={17}
      onContinue={(selections) => {
        setConcernsAnswered(selections.map((selection) => selection.id));
        navigate(nextPath);
      }}
      onClose={openConfirmLeave}
      onBack={() => {
        if (advisor.state === "answered") {
          navigate(MeetYourAIAdvisorContainer_PATH);
        } else {
          navigate(PersonalisationAdvisorBenefitsContainer_PATH);
        }
      }}
      onSkip={() => {
        skipConcerns();
        navigate(nextPath);
      }}
    />
  );
}
